export class Database {
  private db: IDBDatabase;

  private constructor(db: IDBDatabase) {
    this.db = db;
  }

  static create(
    dbName: string,
    upgradeRequest?: (
      db: IDBDatabase,
      oldVersion: number,
      newVersion?: number,
    ) => void | Promise<void>,
    version: number = 1,
  ) {
    return new Promise<Database>((resolve, reject) => {
      const openRequest = indexedDB.open(dbName, version);
      openRequest.addEventListener("upgradeneeded", function (e) {
        upgradeRequest &&
          upgradeRequest(this.result, e.oldVersion, e.newVersion || undefined);
      });
      openRequest.addEventListener("success", function () {
        resolve(new Database(this.result));
      });
      openRequest.addEventListener("error", function () {
        reject(this.error);
      });
    });
  }

  public readCollection<T>(collectionName: string) {
    return new ReadOnlyCollection<T>(this.db, collectionName);
  }

  public readWriteCollection<T>(collectionName: string) {
    return new ReadWriteCollection<T>(this.db, collectionName);
  }
}

export class Collection {
  protected db: IDBDatabase;
  protected collectionName: string;
  protected mode: IDBTransactionMode;

  constructor(
    db: IDBDatabase,
    collectionName: string,
    mode: IDBTransactionMode,
  ) {
    this.db = db;
    this.collectionName = collectionName;
    this.mode = mode;
  }

  protected getTransaction() {
    const tx = this.db.transaction([this.collectionName], this.mode);
    return tx;
  }
}

export class ReadOnlyCollection<T> extends Collection {
  constructor(
    db: IDBDatabase,
    collectionName: string,
    mode: IDBTransactionMode = "readonly",
  ) {
    super(db, collectionName, mode);
  }

  public async get(key: IDBValidKey): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve) => {
      const tx = this.getTransaction();
      const store = tx.objectStore(this.collectionName);

      const getRequest = store.get(key);
      getRequest.addEventListener("success", function () {
        if (!this.result) resolve(undefined);
        else resolve(this.result);
      });
      getRequest.addEventListener("error", function () {
        throw this.error;
      });
    });
  }

  public list(
    query?: IDBValidKey | IDBKeyRange | null | undefined,
    direction?: IDBCursorDirection | undefined,
  ) {
    return new Promise<T[]>((resolve, reject) => {
      const items: T[] = [];
      const tx = this.getTransaction();
      const store = tx.objectStore(this.collectionName);
      const cursor = store.openCursor(query, direction);
      cursor.addEventListener("success", function () {
        if (this.result && this.result.value) {
          items.push(this.result.value);
          this.result.continue();
        } else resolve(items);
      });
      cursor.addEventListener("error", function () {
        reject(this.error);
      });
    });
  }

  public listByIndex(
    indexName: string,
    query?: IDBValidKey | IDBKeyRange | null | undefined,
    direction?: IDBCursorDirection | undefined,
  ) {
    return new Promise<T[]>((resolve, reject) => {
      const items: T[] = [];
      const tx = this.getTransaction();
      const store = tx.objectStore(this.collectionName);
      const index = store.index(indexName);
      const cursor = index.openCursor(query, direction);
      cursor.addEventListener("success", function () {
        if (this.result && this.result.value) {
          items.push(this.result.value);
          this.result.continue();
        } else resolve(items);
      });
      cursor.addEventListener("error", function () {
        reject(this.error);
      });
    });
  }
}

export class ReadWriteCollection<T> extends ReadOnlyCollection<T> {
  constructor(db: IDBDatabase, collectionName: string) {
    super(db, collectionName, "readwrite");
  }

  public set(key: IDBValidKey, data: T): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const tx = this.getTransaction();
      const store = tx.objectStore(this.collectionName);
      const setRequest = store.put(data, key);
      setRequest.onsuccess = function () {
        tx.commit();
        tx.addEventListener("complete", () => {
          resolve();
        });
        tx.addEventListener("error", function () {
          reject(this.error);
        });
      };
      setRequest.onerror = function () {
        reject(this.error);
      };
    });
  }

  public delete(key: IDBValidKey) {
    return new Promise<void>((resolve, reject) => {
      const tx = this.getTransaction();
      const store = tx.objectStore(this.collectionName);
      const request = store.delete(key);
      request.addEventListener("success", () => {
        tx.commit();
        tx.addEventListener("complete", () => {
          resolve();
        });
        tx.addEventListener("error", function () {
          reject(this.error);
        });
      });
      request.addEventListener("error", function () {
        reject(this.error);
      });
    });
  }
}
